import React, { useContext, useState } from 'react';
import Maybe from 'graphql/tsutils/Maybe';
import honeybadger from 'honeybadger-js';
import { OnboardingContext } from '../../../OnboardingContext';
import TrackingContext, { getThemeBaseProps } from '../../../helpers/TrackingContext';
import ThemeContext from '../../../helpers/ThemeContext';
import {
  DatePreference,
  useSignupMutation,
  useSignupUninitializedMutation,
  useUpdateDatePrefMutation,
  useUpdateWeddingWebsiteMutation,
} from '../../../../generated/graphql';
import { ObjectSchema } from 'yup';
import { membershipQuery } from '../../../graphql/v2/queries/membershipQuery';
import { weddingQuery } from '../../../graphql/v2/queries/weddingQuery';
import { weddingWebsiteQuery } from '../../../graphql/v2/queries/weddingWebsiteQuery';
import { set } from 'js-cookie';
import { Formik } from 'formik';
import { DEFAULT_MESSAGE } from '../Alert/index';
import { cleanErrorMessage } from '../../../helpers/cleanErrorMessage';
import { FeatureToggleEnum, useFeatureTogglesContext } from '../../../../context/FeatureTogglesContext';
import { useClient } from '../../../../apollo/ApolloClientsProvider';
import { useFlipperExperimentBucketQueryLazyQuery } from '../../../../generated/graphql';
import { withCurrentQueryParams } from '../../../../helpers/withCurrentQueryParams';

export interface FormProps {
  loading: boolean;
  onLogin?: () => void;
  erroring: boolean;
  errorMessage: string | null;
  onAlertTimeout: () => void;
  values: FormValues;
}

interface FormValues {
  firstName?: Maybe<string>;
  lastName?: Maybe<string>;
  fianceFirstName?: Maybe<string>;
  fianceLastName?: Maybe<string>;
  themeId?: Maybe<number>;
  weddingDate?: Maybe<string>;
  weddingLocation?: Maybe<string>;
  weddingDateTBA?: Maybe<boolean>;
  email?: Maybe<string>;
  password?: Maybe<string>;
  searchable?: Maybe<boolean>;
}

interface CredentialsData {
  email: string;
  password: string;
}

interface BasicInfoData {
  firstName: string;
  lastName: string;
  fianceFirstName: string;
  fianceLastName: string;
  weddingDateTBA: boolean;
  weddingDate?: string;
  weddingLocation?: string;
  searchable?: boolean;
}

interface Props {
  initialValues: any;
  validationSchema: ObjectSchema;
  shouldSignup: boolean;
  Component: React.FC<FormProps>;
}

type SignupAttributes = Omit<BasicInfoData, 'weddingDate' | 'weddingLocation' | 'weddingDateTBA'> & {
  email?: string;
  password?: string;
  brand_version?: string;
  themeId: Maybe<number>;
};

const BRAND_VERSION = '2023.1';

export const AccountCreationForm: React.FC<Props> = ({ initialValues, validationSchema, shouldSignup, Component }) => {
  const {
    onSubmit,
    firstName,
    lastName,
    fianceFirstName,
    fianceLastName,
    isLoggedIn,
    weddingDate,
    weddingDateTBA,
    weddingLocation,
    email,
    password,
    searchable,
    onboardingType,
  } = useContext(OnboardingContext);
  const client = useClient();
  //the isLoggedIn value in the analytics context doesn't get updated and is only initialized when
  //the user starts wws onboarding. So it can be used to determine if the user was already logged in
  //or not and therefore whether they should have seen the offers page already
  const { track, trackBuilderInteraction, identify, isLoggedIn: isExistingUser } = useContext(TrackingContext);
  const { currentColorway, currentColorwayIndex, currentThemeIndex, currentTheme } = useContext(ThemeContext);
  const [signup, { loading: signUpMutationLoading }] = useSignupMutation();
  const [signupUninitialized, { loading: signUpUninitializedLoading }] = useSignupUninitializedMutation();
  const [loading, setLoading] = useState<boolean>(false);

  const isLoading = signUpMutationLoading || signUpUninitializedLoading || loading;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updateWeddingWebsite] = useUpdateWeddingWebsiteMutation();
  const [callFlipperAssignment] = useFlipperExperimentBucketQueryLazyQuery();
  const [updateDatePreferences] = useUpdateDatePrefMutation();
  const { isEnabled } = useFeatureTogglesContext();
  const shouldShowOffers: boolean = isEnabled(FeatureToggleEnum.OnboardingPromoPage);
  let redirectUrl =
    shouldShowOffers && !isExistingUser
      ? '/gateway?target=/gs/dashboard/preview'
      : '/gs/dashboard/preview?source=onboarding';
  // if using AI onboarding, redirect to wws-editor to complete onboarding
  if (onboardingType === 'AI') redirectUrl = '/gs/wedding-website/authenticated/edit';
  // keep inbound marketing params (utm_*, gclid, ...) across the redirect
  redirectUrl = withCurrentQueryParams(redirectUrl);

  const doSignup = async (attributes: SignupAttributes) => {
    if (signUpMutationLoading) return;
    attributes.brand_version = BRAND_VERSION;

    const { data } = await signup({
      variables: {
        attributes,
      },
      update(_, { data }) {
        const cache = client.cache;
        cache.writeQuery({
          query: membershipQuery,
          data: {
            member: data?.signup?.member,
          },
        });
        cache.writeQuery({
          query: weddingQuery,
          data: {
            wedding: data?.signup?.wedding,
          },
        });
        cache.writeQuery({
          query: weddingWebsiteQuery,
          data: {
            weddingWebsite: data?.signup?.weddingWebsite,
          },
        });
      },
    });

    const token = data?.signup?.session?.token;

    if (token) {
      set('xo-session-token', token, { path: '/', domain: '.theknot.com' });
    }

    if (!(window as any).pintrk) {
      (window as any).pintrk = () => {};
    }

    const member = {
      memberId: data?.signup?.member?.id,
      email: data?.signup?.member?.email,
      firstName: data?.signup?.member?.firstName || firstName,
      lastName: data?.signup?.member?.lastName || lastName,
    };

    identify();

    if (!isLoggedIn) {
      track('New Registration', { reason: 'new wedding website' });
    }

    track(
      'Wedding Website Initialized',
      {
        ...(currentColorway
          ? getThemeBaseProps(currentColorway, currentThemeIndex, currentColorwayIndex, currentTheme)
          : {}),
        designer: currentColorway?.themeDesigner?.name,
        hashtag: data?.signup?.weddingWebsite?.slug,
        matchingInvitationUrl: currentColorway?.designerUrl,
        source: 'wws landing page',
        vanityUrl: data?.signup?.weddingWebsite?.vanityUrl,
        wwsTemplateVersion: 'v2',
        tkwwEntities: {
          member,
        },
      },
      {
        context: {
          traits: {
            ...member,
          },
        },
      }
    );

    //bucketing for the user for the AI text button experiment happens here
    //over in the dashboard we only check the user's assignment, never bucket them
    callFlipperAssignment({
      variables: {
        flipperExperimentBucketExperimentId: process.env.REACT_APP_FLIPPER_WWS_AI_TEXT_BUTTON_EXP_ID as string,
        flipperExperimentBucketId: member.memberId as string,
      },
    });
  };

  const doSignupUninitialized = async (attributes: SignupAttributes) => {
    if (signUpUninitializedLoading) return;
    attributes.brand_version = BRAND_VERSION;

    const { data } = await signupUninitialized({
      variables: {
        attributes,
      },
      update(_, { data }) {
        const cache = client.cache;
        cache.writeQuery({
          query: membershipQuery,
          data: {
            member: data?.signupUninitialized?.member,
          },
        });
        cache.writeQuery({
          query: weddingQuery,
          data: {
            wedding: data?.signupUninitialized?.wedding,
          },
        });
        cache.writeQuery({
          query: weddingWebsiteQuery,
          data: {
            weddingWebsite: data?.signupUninitialized?.weddingWebsite,
          },
        });
      },
    });

    const token = data?.signupUninitialized?.session?.token;

    if (token) {
      set('xo-session-token', token, { path: '/', domain: '.theknot.com' });
    }

    if (!(window as any).pintrk) {
      (window as any).pintrk = () => {};
    }

    const member = {
      memberId: data?.signupUninitialized?.member?.id,
      email: data?.signupUninitialized?.member?.email,
      firstName: data?.signupUninitialized?.member?.firstName || firstName,
      lastName: data?.signupUninitialized?.member?.lastName || lastName,
    };

    identify();

    if (!isLoggedIn) {
      track('New Registration', { reason: 'new wedding website' });
    }

    track(
      'Wedding Website Initialized',
      {
        ...(currentColorway
          ? getThemeBaseProps(currentColorway, currentThemeIndex, currentColorwayIndex, currentTheme)
          : {}),
        designer: currentColorway?.themeDesigner?.name,
        hashtag: data?.signupUninitialized?.weddingWebsite?.slug,
        matchingInvitationUrl: currentColorway?.designerUrl,
        source: 'wws landing page',
        vanityUrl: data?.signupUninitialized?.weddingWebsite?.vanityUrl,
        wwsTemplateVersion: 'v2',
        tkwwEntities: {
          member,
        },
      },
      {
        context: {
          traits: {
            ...member,
          },
        },
      }
    );
  };

  const saveLocationAndDatePreferences = async (attributes: BasicInfoData, themeId: Maybe<number> | undefined) => {
    if (attributes.weddingDate && attributes.weddingDate !== '' && !attributes.weddingDateTBA) {
      const weddingDateRequest = {
        date: attributes.weddingDate,
        format: 'natural',
        hideCountdown: false,
        hideDate: false,
        preference: DatePreference.Exact,
        rangeEnd: null,
        rangeStart: null,
        season: null,
        year: null,
      };

      await updateDatePreferences({
        variables: {
          weddingDatePrefAttrs: weddingDateRequest,
        },
      });
    } else if (attributes.weddingDateTBA && attributes.weddingDateTBA === true) {
      await updateDatePreferences({
        variables: {
          weddingDatePrefAttrs: {
            preference: DatePreference.Tba,
          },
        },
      });
    }

    if ((attributes.weddingLocation && attributes.weddingLocation !== '') || attributes.searchable === false) {
      await updateWeddingWebsite({
        variables: {
          attributes: {
            weddingLocation: attributes.weddingLocation,
            themeId: themeId,
            searchable: attributes.searchable,
          },
        },
      });
    }
  };

  const onLogin = async () => {
    const attributes: SignupAttributes = {
      firstName: firstName || '',
      lastName: lastName || '',
      fianceFirstName: fianceFirstName || '',
      fianceLastName: fianceLastName || '',
      themeId: currentColorway?.id,
    };

    onboardingType === 'AI' ? await doSignupUninitialized(attributes) : await doSignup(attributes);

    //onboarding is complete, redirect to couple view or offers page
    window.location.assign(redirectUrl);

    onSubmit({ type: 'ACCOUNT_CREATED', payload: attributes });
  };

  const onSave = async (values: CredentialsData | BasicInfoData) => {
    const attributes = {
      firstName: firstName || '',
      lastName: lastName || '',
      fianceFirstName: fianceFirstName || '',
      fianceLastName: fianceLastName || '',
      weddingDateTBA: weddingDateTBA || false,
      weddingDate: weddingDate || '',
      weddingLocation: weddingLocation || '',
      email: email || '',
      password: password || '',
      searchable: searchable || true,
      ...values,
    };

    // remove the wedding date and location values since they will break the signup call
    const signUpAttributes: SignupAttributes = {
      firstName: attributes.firstName,
      lastName: attributes.lastName,
      fianceFirstName: attributes.fianceFirstName,
      fianceLastName: attributes.fianceLastName,
      themeId: currentColorway?.id,
      email: attributes.email,
      password: attributes.password,
    };

    const trackerBuilderInteractionBaseProps = {
      hasWWSWeddingDate: Boolean(attributes.weddingDate),
      hasWWSWeddingLocation: Boolean(attributes.weddingLocation),
      designName: currentColorway?.name || '',
    };

    if (shouldSignup) {
      setLoading(true);
      if (isLoggedIn) {
        await trackBuilderInteraction('your basic info', {
          ...trackerBuilderInteractionBaseProps,
        });
      }
      try {
        onboardingType === 'AI' ? await doSignupUninitialized(signUpAttributes) : await doSignup(signUpAttributes);
      } catch (error) {
        let message = cleanErrorMessage(error?.toString()) || DEFAULT_MESSAGE;
        honeybadger.notify(error, { message: 'Error signing up', context: { error } });
        track('Error Message Displayed', {
          product: 'wedding websites',
          source: 'account creation',
          message,
        });
        setErrorMessage(message);
        setLoading(false);
        return;
      }
      try {
        await saveLocationAndDatePreferences(attributes, currentColorway?.id);
      } catch (error) {
        let message = cleanErrorMessage(error?.toString()) || DEFAULT_MESSAGE;
        honeybadger.notify(error, { message: 'Error saving location and date preferences', context: { error } });
        track('Error Message Displayed', {
          product: 'wedding websites',
          source: 'account creation',
          message,
        });
        setErrorMessage(message);
        return;
      }

      await trackBuilderInteraction('completed onboarding', {
        ...trackerBuilderInteractionBaseProps,
        selection: 'save',
        builderLocation: attributes.weddingLocation,
        builderWeddingDate: attributes.weddingDate,
        dateSelection: attributes.weddingDateTBA ? 'none' : 'exact',
      });

      window.location.assign(redirectUrl);
      setLoading(false);
      onSubmit({ type: 'ACCOUNT_CREATED', payload: attributes });
      return;
    } else {
      await trackBuilderInteraction('your basic info', {
        ...trackerBuilderInteractionBaseProps,
      });

      onSubmit({ type: 'SUBMIT_BASIC_INFO', payload: attributes });
    }
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={onSave}>
      {({ values }) => (
        <Component
          values={values}
          loading={isLoading}
          onLogin={onLogin}
          erroring={!!errorMessage}
          errorMessage={errorMessage}
          onAlertTimeout={() => {
            setErrorMessage(null);
          }}
        />
      )}
    </Formik>
  );
};
